// Cotizador: partidas del catálogo, descuento por partida y global, totales
// en vivo, autorización por nivel y «convertir en propuesta». Sirve para
// nueva (/cotizaciones/nueva?cuenta=&oportunidad=) y para editar (/cotizaciones/:id).
import * as React from "react";
import { Link, useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { Boton, Campo, Dialog, DialogContenido, DialogDescripcion, DialogDisparador, DialogEncabezado, DialogTitulo, EsqueletoTabla, Input, Pill, PuntoNaranja, Textarea, TituloPagina, useToast, Vacio } from "@iavmx/ui";
import { leer } from "../api";
import { ErrorEnLinea, Kicker, Miga, Seccion } from "../componentes";
import { useEquipo, useProductos, useYo } from "../datos";
import { fechaCorta, fechaHora, hoyIso, mxn } from "../formato";
import { useSesion } from "../sesion";
import { ETIQUETA_ESTADO_COTIZACION, ETIQUETA_NIVEL, type CuentaLista, type Nivel, type Producto, type RespuestaCotizacion, type Umbral } from "../tipos";
import { nivelPara, r2, totalesDe, type Linea } from "../logica/cotizador";

function Catalogo({ productos, onAgregar, desactivado }: { productos: Producto[]; onAgregar: (p: Producto) => void; desactivado: boolean }) {
  const [q, setQ] = React.useState("");
  const f = q.trim().toLowerCase();
  const lista = productos.filter((p) => !f || `${p.nombre} ${p.familia ?? ""} ${p.descripcion ?? ""}`.toLowerCase().includes(f));
  const familias = [...new Set(lista.map((p) => p.familia ?? "Catálogo"))];
  return (
    <div className="space-y-3">
      <label className="block">
        <span className="sr-only">Buscar producto o formato</span>
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar producto o formato" className="h-8 text-base sm:text-sm" />
      </label>
      {lista.length === 0 && <p className="font-ui text-xs text-tinta-tenue">Sin coincidencias en el catálogo.</p>}
      {familias.map((fam) => (
        <div key={fam}>
          <Kicker className="mb-1">{fam}</Kicker>
          <ul className="divide-y divide-linea border-y border-linea">
            {lista
              .filter((p) => (p.familia ?? "Catálogo") === fam)
              .map((p) => (
                <li key={p.id} className="flex items-center justify-between gap-2 py-1.5 font-ui text-sm">
                  <div className="min-w-0">
                    <div className="truncate font-semibold">{p.nombre}</div>
                    <div className="text-xs text-tinta-tenue">{p.precioLista ? mxn(p.precioLista) : "Sin precio en el catálogo"}</div>
                  </div>
                  <Boton tamano="icono" variante="fantasma" aria-label={`Agregar ${p.nombre}`} onClick={() => onAgregar(p)} className="size-8" disabled={desactivado}>
                    <Plus />
                  </Boton>
                </li>
              ))}
          </ul>
        </div>
      ))}
    </div>
  );
}

function ElegirCuenta({ valor, onCambio }: { valor: string; onCambio: (id: string) => void }) {
  const { api, listo } = useSesion();
  const [q, setQ] = React.useState("");
  const lista = useQuery({ queryKey: ["cuentas", q], queryFn: () => api.api.cuentas.$get({ query: q ? { q } : {} }).then((r) => leer<{ cuentas: CuentaLista[] }>(r)), enabled: listo });
  return (
    <Campo etiqueta="Cuenta" htmlFor="cta" ayuda="Escribe para filtrar por razón social o RFC.">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrar cuentas" className="mb-2 h-8 text-base sm:text-sm" aria-label="Filtrar cuentas" />
      <select id="cta" value={valor} onChange={(e) => onCambio(e.target.value)} className="flex h-10 w-full border border-input bg-fondo px-3 font-ui text-base text-tinta sm:text-sm" required>
        <option value="">Elige una cuenta</option>
        {(lista.data?.cuentas ?? []).map((c) => (
          <option key={c.id} value={c.id}>
            {c.razonSocial}
          </option>
        ))}
      </select>
    </Campo>
  );
}

const ORDEN: Nivel[] = ["ejecutivo", "subgerente", "subdirector", "director"];

function lineasDe(d: RespuestaCotizacion): Linea[] {
  return d.partidas.map((p) => ({ clave: p.id, productoId: p.productoId, producto: p.producto, descripcion: p.descripcion, cantidad: Number(p.cantidad), precioLista: Number(p.precioLista), descuentoPct: Number(p.descuentoPct) }));
}

/** El descuento global se reconstruye: lo que falta entre las partidas y el total guardado. */
function globalDe(d: RespuestaCotizacion): number {
  const t = totalesDe(lineasDe(d), 0);
  const total = Number(d.cotizacion.total);
  return t.total > 0 && total < t.total ? r2((1 - total / t.total) * 100) : 0;
}

export function Cotizacion() {
  const params = useParams({ strict: false }) as { id?: string };
  const id = params.id;
  const nueva = !id;
  const { api, listo } = useSesion();
  const q = useQuery({ queryKey: ["cotizacion", id], queryFn: () => api.api.cotizaciones[":id"].$get({ param: { id: id! } }).then((r) => leer<RespuestaCotizacion>(r)), enabled: listo && !nueva });
  const umbralesQ = useQuery({ queryKey: ["umbrales"], queryFn: () => api.api.cotizaciones["-"].umbrales.$get().then((r) => leer<{ umbrales: Umbral[]; porConfirmar: boolean }>(r)), enabled: listo && nueva });
  if (!nueva && q.isLoading) return <EsqueletoTabla />;
  if (!nueva && (q.error || !q.data)) return <Vacio titulo="No se pudo abrir la cotización" texto={q.error?.message} />;
  // Con key, el editor se remonta con los datos frescos después de cada guardado.
  const llave = q.data ? `${q.data.cotizacion.id}:${q.data.cotizacion.total}:${q.data.cotizacion.estado}:${q.data.autorizaciones.length}` : "nueva";
  return <Editor key={llave} id={id} datos={q.data ?? null} umbralesNueva={umbralesQ.data ?? null} />;
}

function Editor({ id, datos, umbralesNueva }: { id: string | undefined; datos: RespuestaCotizacion | null; umbralesNueva: { umbrales: Umbral[]; porConfirmar: boolean } | null }) {
  const busqueda = useSearch({ strict: false }) as { cuenta?: string; oportunidad?: string };
  const nueva = !id;
  const { api } = useSesion();
  const qc = useQueryClient();
  const navegar = useNavigate();
  const { avisar } = useToast();
  const yo = useYo();
  const equipo = useEquipo();
  const productos = useProductos();
  const q = { data: datos };
  const umbralesQ = { data: umbralesNueva };

  const [cuentaId, setCuentaId] = React.useState(busqueda.cuenta ?? "");
  const [lineas, setLineas] = React.useState<Linea[]>(() => (datos ? lineasDe(datos) : []));
  const [global, setGlobal] = React.useState(() => (datos ? globalDe(datos) : 0));
  const [vigencia, setVigencia] = React.useState(() => (datos ? (datos.cotizacion.vigencia ?? "") : hoyIso(21)));
  const [notas, setNotas] = React.useState(() => datos?.cotizacion.notas ?? "");
  const [sucio, setSucio] = React.useState(false);

  const umbrales = q.data?.umbrales ?? umbralesQ.data?.umbrales ?? [];
  const porConfirmar = q.data?.porConfirmar ?? umbralesQ.data?.porConfirmar ?? true;
  const t = totalesDe(lineas, global);
  const nivel = nivelPara(t.descuentoPct, umbrales);
  const miNivel: Nivel = q.data?.miNivel ?? yo.data?.nivel ?? "ejecutivo";
  const autorizadoHasta = (q.data?.autorizaciones ?? []).filter((a) => a.resultado === "autorizada").reduce((m, a) => Math.max(m, Number(a.porcentaje)), 0);
  const pideFirma = nivel !== "ejecutivo" && t.descuentoPct > autorizadoHasta;
  const cerrada = q.data ? ["aceptada", "rechazada", "vencida"].includes(q.data.cotizacion.estado) : false;
  const puedeGuardar = !cerrada && lineas.length > 0 && (nueva ? Boolean(cuentaId) : sucio);

  const cuerpo = () => ({
    vigencia: vigencia || null,
    notas: notas.trim() || null,
    descuentoPct: global,
    partidas: lineas.map((l) => ({ productoId: l.productoId, descripcion: l.descripcion, cantidad: l.cantidad, precioLista: l.precioLista, descuentoPct: l.descuentoPct })),
  });
  const guardar = useMutation({
    mutationFn: async () => {
      if (nueva) {
        const r = await api.api.cotizaciones.$post({ json: { cuentaId, oportunidadId: busqueda.oportunidad ?? null, ...cuerpo() } });
        return leer<{ id: string; folio: string; estado: string }>(r);
      }
      const r = await api.api.cotizaciones[":id"].$put({ param: { id: id! }, json: cuerpo() });
      return { id: id!, ...(await leer<{ ok: boolean; estado: string }>(r)) };
    },
    onSuccess: (r) => {
      setSucio(false);
      avisar({
        titulo: r.estado === "autorizacion_pendiente" ? "Guardada · descuento por autorizar" : "Cotización guardada",
        descripcion: r.estado === "autorizacion_pendiente" ? `Se avisó a ${ETIQUETA_NIVEL[nivel].toLowerCase()}. Mientras, no se convierte en propuesta.` : undefined,
        tono: "exito",
      });
      void qc.invalidateQueries({ queryKey: ["cotizaciones"] });
      void qc.invalidateQueries({ queryKey: ["autorizaciones"] });
      if (nueva) void navegar({ to: "/cotizaciones/$id", params: { id: r.id } });
      else void qc.invalidateQueries({ queryKey: ["cotizacion", id] });
    },
  });
  const convertir = useMutation({
    mutationFn: () => api.api.cotizaciones[":id"].propuesta.$post({ param: { id: id! }, json: {} }).then((r) => leer<{ id: string; existia: boolean }>(r)),
    onSuccess: (r) => {
      avisar({ titulo: r.existia ? "Ya tenía propuesta" : "Propuesta creada", descripcion: "Revisa el texto y mándala desde su ficha.", tono: "exito" });
      void navegar({ to: "/propuestas/$id", params: { id: r.id } });
    },
  });
  const resolver = useMutation({
    mutationFn: (v: { aid: string; resultado: "autorizada" | "rechazada" }) => api.api.cotizaciones[":id"].autorizaciones[":aid"].resolver.$post({ param: { id: id!, aid: v.aid }, json: { resultado: v.resultado } }).then((r) => leer<{ ok: boolean }>(r)),
    onSuccess: () => {
      avisar({ titulo: "Solicitud resuelta", tono: "exito" });
      void qc.invalidateQueries({ queryKey: ["cotizacion", id] });
      void qc.invalidateQueries({ queryKey: ["autorizaciones"] });
    },
    onError: (e) => avisar({ titulo: "No se pudo resolver", descripcion: e.message, tono: "error" }),
  });

  const agregar = (p: Producto) => {
    setLineas((v) => [...v, { clave: crypto.randomUUID(), productoId: p.id, producto: p.nombre, descripcion: p.descripcion ?? p.nombre, cantidad: 1, precioLista: Number(p.precioLista ?? 0), descuentoPct: 0 }]);
    setSucio(true);
  };
  const cambiar = (clave: string, cambios: Partial<Linea>) => {
    setLineas((v) => v.map((l) => (l.clave === clave ? { ...l, ...cambios } : l)));
    setSucio(true);
  };

  const cot = q.data?.cotizacion;
  const pendiente = (q.data?.autorizaciones ?? []).find((a) => a.resultado === "pendiente");
  const ultimaAutorizada = (q.data?.autorizaciones ?? []).find((a) => a.resultado === "autorizada");
  const persona = (n: Nivel) => equipo.data?.equipo.find((p) => (p.puesto ?? "").toLowerCase().includes(n === "subgerente" ? "subgerente" : n === "subdirector" ? "subdirector" : "director comercial"))?.nombre;
  const puedeConvertir = !nueva && !q.data?.propuesta && !sucio && !cerrada && cot?.estado !== "autorizacion_pendiente";
  const motivoNoConvertir = cot?.estado === "autorizacion_pendiente" ? "Espera la autorización del descuento." : sucio ? "Guarda los cambios primero." : undefined;

  return (
    <>
      <Miga partes={[{ texto: "Cotizaciones", to: "/cotizaciones" }, ...(cot ? [{ texto: cot.cuenta, to: "/cuentas/$id", params: { id: cot.cuentaId } }, { texto: cot.folioTexto }] : [{ texto: "nueva" }])]} />
      <TituloPagina
        descripcion={cot ? `${ETIQUETA_ESTADO_COTIZACION[cot.estado]} · ${cot.ejecutivo ?? ""} · ${cot.vigencia ? `vigente hasta el ${fechaCorta(cot.vigencia)}` : "sin vigencia"} · precios del catálogo` : "Elige la cuenta, agrega partidas del catálogo y decide el descuento."}
        accion={
          <div className="flex flex-wrap gap-2">
            {q.data?.propuesta && (
              <Boton variante="contorno" tamano="sm" asChild>
                <Link to="/propuestas/$id" params={{ id: q.data.propuesta.id }}>
                  Ver propuesta
                </Link>
              </Boton>
            )}
            <Boton variante={q.data?.propuesta || nueva ? "acento" : "primario"} tamano="sm" onClick={() => guardar.mutate()} disabled={!puedeGuardar || guardar.isPending}>
              {guardar.isPending ? "Guardando…" : nueva ? "Guardar cotización" : "Guardar cambios"}
            </Boton>
            {!nueva && !q.data?.propuesta && (
              <Boton variante="acento" tamano="sm" onClick={() => convertir.mutate()} disabled={!puedeConvertir || convertir.isPending} title={motivoNoConvertir}>
                Convertir en propuesta
              </Boton>
            )}
          </div>
        }
      >
        {cot ? `Cotización ${cot.folioTexto}` : "Nueva cotización"}
        <PuntoNaranja />
      </TituloPagina>
      {motivoNoConvertir && !nueva && !q.data?.propuesta && <p className="-mt-3 mb-4 text-right font-ui text-xs text-tinta-tenue">{motivoNoConvertir}</p>}

      <div className="grid gap-8 pb-20 lg:grid-cols-[minmax(0,1fr)_340px] lg:pb-0">
        <div className="min-w-0 space-y-6">
          {nueva && <ElegirCuenta valor={cuentaId} onCambio={setCuentaId} />}

          <div className="overflow-x-auto border-y border-linea">
            <table className="w-full min-w-[640px] border-collapse">
              <thead>
                <tr className="text-left font-ui text-[11px] font-semibold uppercase tracking-[.08em] text-tinta-tenue">
                  <th className="py-2 pr-2">Partida</th>
                  <th className="w-20 py-2 pr-2 text-right">Cant.</th>
                  <th className="w-32 py-2 pr-2 text-right">Precio lista</th>
                  <th className="w-20 py-2 pr-2 text-right">Desc. %</th>
                  <th className="w-28 py-2 text-right">Importe</th>
                  <th className="w-10">
                    <span className="sr-only">Quitar</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {t.partidas.map((l) => (
                  <tr key={l.clave} className="border-t border-linea align-top">
                    <td className="py-2 pr-2">
                      <div className="font-titulo text-sm font-semibold">{l.producto ?? "Partida"}</div>
                      <Input value={l.descripcion} onChange={(e) => cambiar(l.clave, { descripcion: e.target.value })} aria-label="Descripción de la partida" className="mt-1 h-8 text-base sm:text-xs" disabled={cerrada} />
                    </td>
                    <td className="py-2 pr-2">
                      <Input type="number" min={0} step={1} inputMode="numeric" value={l.cantidad} onChange={(e) => cambiar(l.clave, { cantidad: Number(e.target.value) })} aria-label="Cantidad" className="h-8 text-right text-base sm:text-sm" disabled={cerrada} />
                    </td>
                    <td className="py-2 pr-2">
                      <Input type="number" min={0} step={100} inputMode="numeric" value={l.precioLista} onChange={(e) => cambiar(l.clave, { precioLista: Number(e.target.value) })} aria-label="Precio de lista" className="h-8 text-right text-base sm:text-sm" disabled={cerrada} />
                    </td>
                    <td className="py-2 pr-2">
                      <Input type="number" min={0} max={100} step={1} inputMode="numeric" value={l.descuentoPct} onChange={(e) => cambiar(l.clave, { descuentoPct: Number(e.target.value) })} aria-label="Descuento por partida en porcentaje" className="h-8 text-right text-base sm:text-sm" disabled={cerrada} />
                    </td>
                    <td className="py-2 text-right font-titulo text-sm font-bold tabular-nums">{mxn(l.importe)}</td>
                    <td className="py-2 text-right">
                      <Boton
                        tamano="icono"
                        variante="fantasma"
                        className="size-8"
                        aria-label={`Quitar ${l.producto ?? "partida"}`}
                        onClick={() => {
                          setLineas((v) => v.filter((x) => x.clave !== l.clave));
                          setSucio(true);
                        }}
                        disabled={cerrada}
                      >
                        <Trash2 />
                      </Boton>
                    </td>
                  </tr>
                ))}
                {lineas.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-6 text-center font-ui text-sm text-tinta-tenue">
                      Sin partidas. Agrega productos desde el catálogo.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_300px]">
            <div className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <Campo etiqueta="Descuento global (%)" htmlFor="dg" ayuda="Sobre lo que queda tras el descuento por partida.">
                  <Input
                    id="dg"
                    type="number"
                    min={0}
                    max={100}
                    step={1}
                    inputMode="numeric"
                    value={global}
                    onChange={(e) => {
                      setGlobal(Number(e.target.value));
                      setSucio(true);
                    }}
                    className="text-base sm:text-sm"
                    disabled={cerrada}
                  />
                </Campo>
                <Campo etiqueta="Vigencia" htmlFor="vig">
                  <Input
                    id="vig"
                    type="date"
                    value={vigencia}
                    onChange={(e) => {
                      setVigencia(e.target.value);
                      setSucio(true);
                    }}
                    className="text-base sm:text-sm"
                    disabled={cerrada}
                  />
                </Campo>
              </div>
              <Campo etiqueta="Notas para la propuesta" htmlFor="nt" ayuda="Aparecen en «Lo que buscas» de la propuesta viva.">
                <Textarea
                  id="nt"
                  value={notas}
                  onChange={(e) => {
                    setNotas(e.target.value);
                    setSucio(true);
                  }}
                  className="min-h-20 text-base sm:text-sm"
                  disabled={cerrada}
                  placeholder="Cuarenta unidades vendidas durante el Buen Fin, con tráfico a las dos sucursales…"
                />
              </Campo>
              <ErrorEnLinea error={guardar.error ?? convertir.error} />
            </div>
            <dl className="space-y-1 font-ui text-sm" aria-label="Totales">
              <div className="flex justify-between border-t border-linea py-1.5">
                <dt>Precio de lista</dt>
                <dd className="font-titulo tabular-nums">{mxn(t.subtotal)}</dd>
              </div>
              <div className="flex justify-between border-t border-linea py-1.5">
                <dt>Descuento {t.descuentoPct} %</dt>
                <dd className="font-titulo tabular-nums">−{mxn(t.descuentoMonto)}</dd>
              </div>
              <div className="flex justify-between border-t border-linea py-1.5">
                <dt>Subtotal</dt>
                <dd className="font-titulo tabular-nums">{mxn(t.total)}</dd>
              </div>
              <div className="flex justify-between border-t border-linea py-1.5">
                <dt>IVA 16 %</dt>
                <dd className="font-titulo tabular-nums">{mxn(t.iva)}</dd>
              </div>
              <div className="flex items-baseline justify-between border-t-2 border-tinta py-2">
                <dt className="font-semibold">Total</dt>
                <dd className="font-titulo text-2xl font-black tabular-nums">{mxn(t.conIva)}</dd>
              </div>
            </dl>
          </div>

          <Seccion titulo="Autorización de descuento">
            <div className="flex flex-wrap items-center gap-2">
              {t.descuentoPct <= 0 ? (
                <Pill tono="neutro">Sin descuento</Pill>
              ) : pendiente && !sucio ? (
                <Pill tono="ambar">Pendiente · {ETIQUETA_NIVEL[pendiente.nivelRequerido].toLowerCase()}</Pill>
              ) : pideFirma ? (
                <Pill tono="ambar">Pide firma de {ETIQUETA_NIVEL[nivel].toLowerCase()}</Pill>
              ) : (
                <Pill tono="verde">{nivel === "ejecutivo" ? "Dentro de tu nivel" : "Autorizado"}</Pill>
              )}
              {porConfirmar && <Pill tono="azul">Umbrales por confirmar</Pill>}
            </div>
            <p className="font-ui text-sm text-tinta-3">
              {pendiente && !sucio
                ? `El ${Number(pendiente.porcentaje)} % está esperando la firma de ${ETIQUETA_NIVEL[pendiente.nivelRequerido].toLowerCase()}${persona(pendiente.nivelRequerido) ? ` (${persona(pendiente.nivelRequerido)})` : ""}. Mientras, la cotización no se convierte en propuesta.`
                : ultimaAutorizada && t.descuentoPct <= autorizadoHasta && t.descuentoPct > 0
                  ? `El ${Number(ultimaAutorizada.porcentaje)} % lo autorizó ${ultimaAutorizada.autorizadoPor ?? "gerencia"} el ${fechaHora(ultimaAutorizada.resueltaEn)}. Si subes el descuento, la propuesta vuelve a borrador y se pide la firma del siguiente nivel.`
                  : pideFirma
                    ? `Un ${t.descuentoPct} % lo firma ${ETIQUETA_NIVEL[nivel].toLowerCase()}${persona(nivel) ? ` (${persona(nivel)})` : ""}${ORDEN.indexOf(miNivel) >= ORDEN.indexOf(nivel) ? "; con tu nivel queda autorizado al guardar." : ". Al guardar se le avisa; tú puedes seguir editando."}`
                    : t.descuentoPct > 0
                      ? `Un ${t.descuentoPct} % está dentro de lo que un ejecutivo puede dar sin firma.`
                      : "Sin descuento no hace falta autorización."}
            </p>
            {pendiente && q.data?.puedoFirmar && !sucio && (
              <div className="flex gap-2">
                <Boton tamano="sm" variante="contorno" onClick={() => resolver.mutate({ aid: pendiente.id, resultado: "rechazada" })} disabled={resolver.isPending}>
                  Rechazar
                </Boton>
                <Boton tamano="sm" onClick={() => resolver.mutate({ aid: pendiente.id, resultado: "autorizada" })} disabled={resolver.isPending}>
                  Autorizar el {Number(pendiente.porcentaje)} %
                </Boton>
              </div>
            )}
            {(q.data?.autorizaciones.length ?? 0) > 0 && (
              <Dialog>
                <DialogDisparador asChild>
                  <Boton variante="liga" tamano="sm">
                    Ver historial
                  </Boton>
                </DialogDisparador>
                <DialogContenido>
                  <DialogEncabezado>
                    <DialogTitulo>Historial de autorizaciones</DialogTitulo>
                    <DialogDescripcion>Cada solicitud, quién la firmó y cuándo.</DialogDescripcion>
                  </DialogEncabezado>
                  <ul className="divide-y divide-linea font-ui text-sm">
                    {q.data!.autorizaciones.map((a) => (
                      <li key={a.id} className="py-2">
                        <div className="flex justify-between gap-2">
                          <span className="font-semibold">
                            {Number(a.porcentaje)} % · {ETIQUETA_NIVEL[a.nivelRequerido].toLowerCase()}
                          </span>
                          <Pill tono={a.resultado === "autorizada" ? "verde" : a.resultado === "rechazada" ? "rojo" : "ambar"}>{a.resultado}</Pill>
                        </div>
                        <div className="text-xs text-tinta-tenue">
                          Pedida {fechaHora(a.creadoEn)}
                          {a.resueltaEn ? ` · resuelta ${fechaHora(a.resueltaEn)} por ${a.autorizadoPor ?? "—"}` : ""}
                          {a.comentario ? ` · «${a.comentario}»` : ""}
                        </div>
                      </li>
                    ))}
                  </ul>
                </DialogContenido>
              </Dialog>
            )}
          </Seccion>
        </div>

        <aside className="min-w-0 space-y-8">
          <Seccion titulo="Quién autoriza">
            <table className="w-full font-ui text-sm">
              <tbody>
                {ORDEN.map((n) => {
                  const u = umbrales.find((x) => x.nivel === n);
                  const rango = !u ? (n === "ejecutivo" ? "sin descuento" : "por confirmar") : u.pctMax === null ? `más de ${Number(u.pctMin)} %` : Number(u.pctMin) === 0 ? `hasta ${Number(u.pctMax)} %` : `${Number(u.pctMin)} – ${Number(u.pctMax)} %`;
                  return (
                    <tr key={n} className={`border-t border-linea ${nivel === n && t.descuentoPct > 0 ? "font-semibold" : ""}`}>
                      <td className="py-1.5">
                        {ETIQUETA_NIVEL[n]}
                        {n !== "ejecutivo" && persona(n) && <span className="text-tinta-tenue"> · {persona(n)}</span>}
                      </td>
                      <td className="py-1.5 text-right tabular-nums">{rango}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {porConfirmar && <p className="font-ui text-xs text-tinta-tenue">Umbrales de ejemplo. El intake no los definió; se fijan en la sesión de cierre. Mientras, se aplican igual para no bloquear.</p>}
          </Seccion>
          <span id="catalogo" className="block scroll-mt-4" />
          <Seccion titulo="Catálogo" accion={<Pill tono="neutro">{productos.data?.sinPrecios ? "sin precios" : "comercial.productos"}</Pill>}>
            {productos.isLoading ? <EsqueletoTabla filas={4} columnas={2} /> : <Catalogo productos={productos.data?.productos ?? []} onAgregar={agregar} desactivado={cerrada} />}
            {productos.data?.sinPrecios && <p className="font-ui text-xs text-tinta-tenue">El catálogo no trae precios todavía (viven en la hoja de Google de Comercial); captúralos en cada partida.</p>}
          </Seccion>
        </aside>
      </div>
      {/* Celular: total y acción principal siempre a la mano; el catálogo queda abajo. */}
      <div className="fixed inset-x-0 bottom-0 z-10 flex items-center gap-3 border-t border-linea-fuerte bg-fondo px-4 py-2 lg:hidden">
        <div className="min-w-0 flex-1">
          <div className="font-titulo text-lg font-black tabular-nums leading-none">{mxn(t.conIva)}</div>
          <div className="truncate font-ui text-xs text-tinta-tenue">{lineas.length} partida{lineas.length === 1 ? "" : "s"} · {t.descuentoPct > 0 ? `${t.descuentoPct} % desc.` : "sin descuento"} · <a href="#catalogo" className="text-azul">catálogo</a></div>
        </div>
        {puedeConvertir ? (
          <Boton variante="acento" tamano="sm" onClick={() => convertir.mutate()} disabled={convertir.isPending}>
            Convertir en propuesta
          </Boton>
        ) : (
          <Boton variante="acento" tamano="sm" onClick={() => guardar.mutate()} disabled={!puedeGuardar || guardar.isPending}>
            {guardar.isPending ? "Guardando…" : "Guardar"}
          </Boton>
        )}
      </div>
    </>
  );
}
